
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { PlusCircle, X, ShieldAlert } from "lucide-react";
import { useJobs } from "@/contexts/JobsContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Job } from "@/types/job";

// Helper function to sanitize strings against XSS
const sanitizeString = (str: string): string => {
  if (!str) return '';
  return str
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
    .replace(/\(/g, '&#040;')
    .replace(/\)/g, '&#041;')
    .replace(/{/g, '&#123;')
    .replace(/}/g, '&#125;');
};

// Enhanced form schema with sanitation and strict validation
const formSchema = z.object({
  title: z.string()
    .min(3, "Job title must be at least 3 characters")
    .max(100, "Job title cannot exceed 100 characters")
    .refine(val => !/[<>{}()[\]\/\\]/.test(val), {
      message: "Invalid characters detected"
    }),
  areaId: z.string().min(1, "Please select an area"),
  department: z.string()
    .min(1, "Department is required")
    .max(50, "Department name too long")
    .refine(val => !/[<>{}()[\]\/\\]/.test(val), {
      message: "Invalid characters detected"
    }),
  location: z.string()
    .min(1, "Location is required")
    .max(100, "Location name too long")
    .refine(val => !/[<>{}()[\]\/\\]/.test(val), {
      message: "Invalid characters detected"
    }),
  remote: z.boolean().default(false),
  type: z.string().min(1, "Job type is required"),
  salary: z.string()
    .min(1, "Salary range is required")
    .max(50, "Salary range too long")
    .refine(val => !/[<>{}()[\]\/\\]/.test(val), {
      message: "Invalid characters detected"
    }),
  description: z.string()
    .min(50, "Description must be at least 50 characters")
    .max(5000, "Description is too long")
    .refine(val => !/(<script|javascript:|onerror=|onload=)/i.test(val), {
      message: "Potentially unsafe content detected"
    }),
});

type FormValues = z.infer<typeof formSchema>;

interface AddJobDialogProps {
  buttonClassName?: string;
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

export default function AddJobDialog({ 
  buttonClassName = "", 
  variant = "default",
  size = "default"
}: AddJobDialogProps) {
  const [open, setOpen] = useState(false);
  const [securityAlert, setSecurityAlert] = useState<string | null>(null);
  const { addJob, areas } = useJobs();
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      areaId: "",
      department: "",
      location: "",
      remote: false,
      type: "Full-time",
      salary: "",
      description: "",
    },
  });

  // Security measures: Rate limiting
  const [lastSubmitTime, setLastSubmitTime] = useState<number>(0);
  const [submitCount, setSubmitCount] = useState<number>(0);

  function onSubmit(data: FormValues) {
    try {
      // Rate limiting: prevent too many submissions 
      const now = Date.now();
      if (now - lastSubmitTime < 1000) {
        setSecurityAlert("Please wait before submitting again");
        return;
      }
      
      if (submitCount > 10) {
        setSecurityAlert("Too many submissions detected. Please try again later.");
        toast.error("Security alert: submission rate limit exceeded");
        return;
      }
      
      setLastSubmitTime(now);
      setSubmitCount(prev => prev + 1);
      
      // Sanitize all inputs before storing
      const sanitizedData = {
        ...data,
        title: sanitizeString(data.title),
        department: sanitizeString(data.department),
        location: sanitizeString(data.location),
        salary: sanitizeString(data.salary),
        description: sanitizeString(data.description)
      };
      
      addJob({
        ...sanitizedData,
        status: 'active',
      });
      
      toast.success("Job added successfully!");
      form.reset();
      setOpen(false);
      setSecurityAlert(null);
    } catch (error) {
      console.error("Error adding job:", error);
      toast.error("Failed to add job. Please try again.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant={variant} size={size} className={buttonClassName}>
          <PlusCircle className="mr-2 h-4 w-4" />
          Add Job
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Add New Job</DialogTitle>
          <DialogDescription>
            Fill in the details to create a new job listing.
          </DialogDescription>
        </DialogHeader>
        
        {securityAlert && (
          <Alert variant="destructive" className="my-4">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Security Alert</AlertTitle>
            <AlertDescription>{securityAlert}</AlertDescription>
          </Alert>
        )}
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Senior Frontend Developer" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="areaId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Area</FormLabel>
                    <FormControl>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        {...field}
                      >
                        <option value="">Select an area</option>
                        {areas.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))}
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="department"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Department</FormLabel>
                    <FormControl>
                      <Input placeholder="Engineering" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input placeholder="San Francisco, CA" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Job Type</FormLabel>
                    <FormControl>
                      <select
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        {...field}
                      >
                        <option value="Full-time">Full-time</option>
                        <option value="Part-time">Part-time</option>
                        <option value="Contract">Contract</option>
                        <option value="Internship">Internship</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <FormField
                control={form.control}
                name="salary"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Salary Range</FormLabel>
                    <FormControl>
                      <Input placeholder="₹100,000 - ₹130,000" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            
            <FormField
              control={form.control}
              name="remote"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Remote Position</FormLabel>
                    <FormDescription>
                      Is this a remote-friendly position?
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Job Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Detailed description of the job responsibilities, requirements, and benefits..."
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" variant="outline">Cancel</Button>
              </DialogClose>
              <Button type="submit">Create Job</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
